import { useState, useRef, useCallback } from 'react';
import { auth } from '../services/firebase';
import Constants from 'expo-constants';
import Toast from 'react-native-toast-message';

const projectId = Constants.manifest.extra.projectId;
const FUNCTION_URL =
  `https://us-central1-${projectId}.cloudfunctions.net/downloadStorageZipV2`;

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function useDownloadStorageZip() {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState('');
  const abortRef = useRef(null);

  const downloadStorageAsZip = useCallback(async () => {
    if (downloading) return;

    if (!auth.currentUser) {
      Toast.show({ type: 'error', text1: 'Usuário não autenticado.' });
      return;
    }

    setDownloading(true);
    setProgress('Preparando...');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await auth.currentUser.getIdToken();

      setProgress('Gerando ZIP no servidor...');

      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao gerar o arquivo ZIP.');
      }

      const contentLength = response.headers.get('Content-Length');
      const totalSize = contentLength ? parseInt(contentLength, 10) : null;

      if (!response.body || typeof response.body.getReader !== 'function') {
        setProgress('Baixando...');
        const blob = await response.blob();
        triggerBlobDownload(blob, 'storage-materiais.zip');
      } else {
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(value);
          received += value.length;

          if (totalSize) {
            const pct = Math.round((received / totalSize) * 100);
            setProgress(`Baixando... ${pct}% (${formatBytes(received)})`);
          } else {
            setProgress(`Baixando... ${formatBytes(received)}`);
          }
        }

        const blob = new Blob(chunks, { type: 'application/zip' });
        triggerBlobDownload(blob, 'storage-materiais.zip');
      }

      setProgress('');
      Toast.show({ type: 'success', text1: 'Download concluído!' });
    } catch (error) {
      if (error.name === 'AbortError') {
        Toast.show({ type: 'info', text1: 'Download cancelado.' });
      } else {
        Toast.show({
          type: 'error',
          text1: 'Erro ao exportar',
          text2: error.message || 'Tente novamente.',
        });
      }
      setProgress('');
    } finally {
      abortRef.current = null;
      setDownloading(false);
    }
  }, [downloading]);

  const cancelDownload = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  return { downloading, progress, downloadStorageAsZip, cancelDownload };
}
